import { API_BASE } from "@/lib/api";
import { useEffect, useState } from "react";
import { CalendarPlus, Copy, RotateCcw, Smartphone, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * iCal-prenumeration: visar en webcal://-URL per barn så de kan lägga in
 * sina kalendrar (skola + studiepass) i mobilens kalender-app.
 */
export function IcalShareCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ical/me");
      setItems(data.items || []);
    } catch (e) {
      // Tysta — sektion göms om endpoint inte svarar
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // iCal-URL ska peka på frontend-domänen (snyggare än backend-URL:n) och
  // proxas via vår Node-server till backend.
  const fullUrl = (path) => {
    if (typeof window !== "undefined" && window.location && window.location.origin) {
      return `${window.location.origin}${path}`;
    }
    return `${API_BASE || ""}${path}`;
  };
  const webcalUrl = (path) => {
    const u = fullUrl(path);
    return u.replace(/^https?:\/\//, "webcal://");
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
      toast.success("Kopierat — klistra in i kalenderappen");
    } catch {
      toast.error("Kunde inte kopiera");
    }
  };

  const rotate = async (userId) => {
    if (!window.confirm("Skapa en ny iCal-URL? Den gamla slutar fungera direkt.")) return;
    try {
      await api.post(`/ical/me/rotate/${userId}`);
      toast.success("Ny URL skapad");
      await load();
    } catch (e) {
      toast.error("Kunde inte rotera");
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="card-soft p-3 mb-4" data-testid="ical-share-card">
      <div className="flex items-start gap-2.5">
        <CalendarPlus size={14} className="text-[color:var(--moss)] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <p className="text-xs font-medium">Prenumerera i mobilkalendern</p>
            <p className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5">
              Lägg till länken i iPhone/Android-kalendern så syns prov, läxor och studiepass direkt — uppdateras automatiskt var 15:e minut.
            </p>
          </div>
          {items.map((it) => {
            const wc = webcalUrl(it.ical_path);
            const httpsUrl = fullUrl(it.ical_path);
            return (
              <div
                key={it.user_id}
                className="rounded-xl border border-[color:var(--border-soft)] p-2.5 bg-[color:var(--bg-elevated)] space-y-2"
                data-testid={`ical-row-${it.user_id}`}
              >
                <p className="text-xs font-medium flex items-center gap-1.5">
                  <Smartphone size={11} /> {it.user_name}
                </p>
                <code className="text-[10px] text-[color:var(--text-secondary)] break-all block leading-snug">
                  {wc}
                </code>
                <div className="flex gap-1.5 flex-wrap">
                  <a
                    href={wc}
                    className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full bg-[color:var(--moss)] text-white hover:bg-[#1f421f] transition"
                    data-testid={`ical-open-${it.user_id}`}
                  >
                    Öppna i kalender
                  </a>
                  <button
                    onClick={() => copy(`wc-${it.user_id}`, wc)}
                    className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full border border-[color:var(--border-soft)] hover:bg-[color:var(--bg-soft)] transition"
                    data-testid={`ical-copy-${it.user_id}`}
                  >
                    {copiedKey === `wc-${it.user_id}` ? <Check size={10} /> : <Copy size={10} />}
                    Kopiera
                  </button>
                  <button
                    onClick={() => copy(`https-${it.user_id}`, httpsUrl)}
                    className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full border border-[color:var(--border-soft)] hover:bg-[color:var(--bg-soft)] transition"
                    data-testid={`ical-copy-https-${it.user_id}`}
                  >
                    {copiedKey === `https-${it.user_id}` ? <Check size={10} /> : <Copy size={10} />}
                    HTTPS-URL
                  </button>
                  <button
                    onClick={() => rotate(it.user_id)}
                    className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full border border-[#DC2626]/30 text-[#DC2626] hover:bg-[#FEE2E2] transition"
                    data-testid={`ical-rotate-${it.user_id}`}
                    title="Rotera token om någon olovligen fått tag i den"
                  >
                    <RotateCcw size={10} /> Ny URL
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
